import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-forgot-password',
    templateUrl: './forgot-password.component.html',
    styleUrls: ['./forgot-password.component.css']
})
export class ForgotPasswordComponent implements OnInit {
    forgotPasswordForm!: FormGroup;
    isTranslationsReady = false;
    isSubmitting = false;
    showPassword = false;
    showConfirmPassword = false;
    countdown = 0;

    @Input() isCodeSent: boolean = false;
    @Output() onSendCode = new EventEmitter<{ email: string }>();
    @Output() onSubmit = new EventEmitter<{ email: string, code: string, newPassword: string }>();
    @Output() modeChange = new EventEmitter<void>();

    constructor(private fb: FormBuilder, private translate: TranslateService) {
        this.initForm();
        this.initTranslations();
    }

    private initForm() {
        this.forgotPasswordForm = this.fb.group({
            email: ['', [Validators.required, Validators.email], [this.noSpecialSymbolValidator, this.noWhitespaceValidator, this.noChineseValidator]],
            code: [''],
            newPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20), this.noWhitespaceValidator, this.noChineseValidator]],
            confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20), this.noWhitespaceValidator, this.noChineseValidator]],
        });
    }

    private initTranslations() {
        this.translate.setDefaultLang('zh-CN');
        const savedLang = localStorage.getItem('selectedLanguage');
        if (savedLang) {
            this.translate.use(savedLang);
        } else {
            const browserLang = this.translate.getBrowserLang();
            const lang = browserLang?.match(/zh|en|ru/) ?
                (browserLang === 'zh' ? 'zh-CN' : browserLang === 'en' ? 'en-US' : 'ru-RU') :
                'zh-CN';
            this.translate.use(lang);
            localStorage.setItem('selectedLanguage', lang);
        }
    }

    private noWhitespaceValidator(control: FormControl) {
        const isWhitespace = (control.value || '').indexOf(' ') !== -1;
        return isWhitespace ? { whitespace: true } : null;
    }

    private noChineseValidator(control: FormControl) {
        const value = control.value || '';
        return /[\u4e00-\u9fa5]/.test(value) ? { chinese: true } : null;
    }

    private noSpecialSymbolValidator(control: FormControl) {
        const emailDomainMatcher = (control.value || '').split('@');
        if (emailDomainMatcher.length > 1) {
            const specialSymbol = emailDomainMatcher[0].indexOf('.') !== -1 || emailDomainMatcher[0].indexOf('+') !== -1;
            if (emailDomainMatcher[1] === 'foxit.io') {
                return null;
            }
            return specialSymbol ? { specialSymbol: true } : null;
        }
        return null;
    }


    isFieldInvalid(fieldName: string): boolean {
        const field = this.forgotPasswordForm.get(fieldName);
        return field ? (field.invalid && (field.dirty || field.touched)) : false;
    }

    getErrorMessage(field: string): string {
        const control = this.forgotPasswordForm.get(field);
        if (!control) return '';

        if (control.hasError('required')) {
            switch (field) {
                case 'email':
                    return this.translate.instant('Common.Error.emailRequired');
                case 'code':
                    return this.translate.instant('Auth.form.codeRequired');
                case 'newPassword':
                case 'confirmPassword':
                    return this.translate.instant('Common.Error.passwordRequired');
                default:
                    return '';
            }
        }

        if (control.hasError('email')) {
            return this.translate.instant('Common.Error.emailInvalid');
        }

        if (control.hasError('minlength')) {
            if (field === 'code') {
                return this.translate.instant('Common.Error.codeLength');
            }
            return this.translate.instant('Common.Error.passwordMinLength');
        }

        if (control.hasError('maxlength')) {
            if (field === 'code') {
                return this.translate.instant('Common.Error.codeLength');
            }
            return this.translate.instant('Common.Error.passwordMaxLength');
        }

        if (control.hasError('pattern') && field === 'newPassword') {
            return this.translate.instant('Common.Error.noWhitespace');
        }

        if (control.hasError('mismatch') && field === 'confirmPassword') {
            return this.translate.instant('Auth.form.passwordMismatch');
        }

        if (control.hasError('specialSymbol')) {
            return this.translate.instant('Common.Error.specialSymbol');
        }

        if (control.hasError('whitespace')) {
            return this.translate.instant('Common.Error.noWhitespace');
        }

        if (control.hasError('chinese')) {
            return this.translate.instant('Common.Error.noChinese');
        }

        return '';
    }

    updateFormValidators() {
        const codeControl = this.forgotPasswordForm.get('code');
        const newPasswordControl = this.forgotPasswordForm.get('newPassword');
        const confirmPasswordControl = this.forgotPasswordForm.get('confirmPassword');

        if (codeControl && newPasswordControl && confirmPasswordControl) {
            codeControl.setValidators([Validators.required, Validators.minLength(6), Validators.maxLength(6)]);
            newPasswordControl.setValidators([
                Validators.required,
                Validators.minLength(8),
                Validators.maxLength(20),
                Validators.pattern(/^\S*$/)
            ]);
            confirmPasswordControl.setValidators([Validators.required]);

            codeControl.updateValueAndValidity();
            newPasswordControl.updateValueAndValidity();
            confirmPasswordControl.updateValueAndValidity();
        }
    }

    checkPasswordMatch() {
        const newPassword = this.forgotPasswordForm.get('newPassword')?.value;
        const confirmPassword = this.forgotPasswordForm.get('confirmPassword')?.value;

        if (newPassword && confirmPassword) {
            if (newPassword !== confirmPassword) {
                this.forgotPasswordForm.get('confirmPassword')?.setErrors({ 'mismatch': true });
            } else {
                const errors = this.forgotPasswordForm.get('confirmPassword')?.errors;
                if (errors && 'mismatch' in errors) {
                    delete errors['mismatch'];

                    if (Object.keys(errors).length === 0) {
                        this.forgotPasswordForm.get('confirmPassword')?.setErrors(null);
                    } else {
                        this.forgotPasswordForm.get('confirmPassword')?.setErrors(errors);
                    }
                }
            }
        }
    }

    handleSendCode(): void {
        if (!this.forgotPasswordForm.get('email')?.errors) {
            this.onSendCode.emit({ email: this.forgotPasswordForm.get('email')?.value });
            this.updateFormValidators();

            this.forgotPasswordForm.get('newPassword')?.valueChanges.subscribe(() => this.checkPasswordMatch());
            this.forgotPasswordForm.get('confirmPassword')?.valueChanges.subscribe(() => this.checkPasswordMatch());

            this.startCountdown();
        } else {
            this.forgotPasswordForm.get('email')?.markAsTouched();
        }
    }

    handleSubmit(): void {
        if (this.forgotPasswordForm.valid && !this.isSubmitting) {
            this.isSubmitting = true;
            const { email, code, newPassword } = this.forgotPasswordForm.value;
            this.onSubmit.emit({ email, code, newPassword });
            setTimeout(() => this.isSubmitting = false, 1000);
        } else {
            Object.keys(this.forgotPasswordForm.controls).forEach(key => {
                const control = this.forgotPasswordForm.get(key);
                if (control) {
                    control.markAsTouched();
                }
            });
        }
    }

    togglePassword(field: 'newPassword' | 'confirmPassword'): void {
        if (field === 'newPassword') {
            this.showPassword = !this.showPassword;
        } else {
            this.showConfirmPassword = !this.showConfirmPassword;
        }
    }

    private startCountdown(): void {
        this.countdown = 60;
        const timer = setInterval(() => {
            this.countdown--;
            if (this.countdown <= 0) {
                clearInterval(timer);
            }
        }, 1000);
    }

    ngOnInit(): void {
        this.translate.get('Auth.form.emailInput').subscribe(() => {
            this.isTranslationsReady = true;
        });
    }
} 